import React from 'react';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { notifications, extractApiErrorMessage } from '../../utils';
import { formatDateTime } from '../../utils/dateHelper';
import { Title, LegacyGrid as Grid } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';
import {
  Chip,
  Icon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@mui/material';
import { tokens } from '../../theme/tokens';
import { V4_TABLE } from '../../theme/v4Compat';
import { HealthSystemDomainAnimalsModelsAnimalHealthLogViewModel } from '../../api/HealthSystemApi';
import { AnimalHealthLogType, AnimalHealthLogTypeTranslations } from '../../models/common';

interface AnimalViewHealthLogsProps {
  animalId: string;
}

const AnimalViewHealthLogs: React.FC<AnimalViewHealthLogsProps> = ({ animalId }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [logs, setLogs] = React.useState<HealthSystemDomainAnimalsModelsAnimalHealthLogViewModel[]>([]);
  const [total, setTotal] = React.useState(0);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const loadHealthLogs = async () => {
    if (!animalId) {
      return;
    }

    setIsBusy(true);

    try {
      const result = await apiService.api.animals.animalsGetHealthLogs({
        id: animalId,
        pageIndex: page + 1,
        pageSize: rowsPerPage
      });

      setLogs(result.data.items || []);
      setTotal(result.data.totalCount || 0);
    } catch (e: unknown) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setLoaded(true);
      setIsBusy(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  // Load data when loaded flag changes
  React.useEffect(() => {
    if (!loaded) {
      loadHealthLogs();
    }
  }, [loaded]);

  // Reset state when animalId changes
  React.useEffect(() => {
    setLogs([]);
    setTotal(0);
    setPage(0);
    setLoaded(false);
  }, [animalId]);

  const renderTypeChip = (log: HealthSystemDomainAnimalsModelsAnimalHealthLogViewModel) => {
    const type = log.type as unknown as AnimalHealthLogType;
    const label = t(AnimalHealthLogTypeTranslations[type] ?? 'f-animal-health-log-type-other');

    switch (type) {
      case AnimalHealthLogType.Sick:
        return (
          // v4 left the small font icon at 20px in a small chip; v9 shrinks it to 18px.
          <Chip
            size="small"
            variant="outlined"
            sx={{ '& .MuiChip-icon': { fontSize: 20 } }}
            icon={<Icon fontSize="small">coronavirus</Icon>}
            label={label}
          />
        );

      case AnimalHealthLogType.DiedAnimal:
      case AnimalHealthLogType.DiedByLocation:
        return <Chip size="small" variant="outlined" color="secondary" label={label} />;

      default:
        return <Chip size="small" variant="outlined" label={label} />;
    }
  };

  const renderDisease = (log: HealthSystemDomainAnimalsModelsAnimalHealthLogViewModel) => {
    if (!log.disease) {
      return '-';
    }

    return (
      <span className="inline-flex items-center gap-1.5">
        {log.diseaseColour && (
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: log.diseaseColour }} />
        )}
        {log.disease}
      </span>
    );
  };

  return (
    <Paper elevation={1} sx={{ p: 2, display: 'flex', overflow: 'auto', flexDirection: 'column', minHeight: 200 }}>
      <Grid container spacing={1}>
        <Grid container direction="row">
          <Grid container item spacing={1} xs={10}>
            <Grid item>
              <Icon color="primary">{'monitor_heart'}</Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-animal-health-log-title')}</Title>
            </Grid>
            <Grid item>
              <Chip size="small" variant="outlined" label={`${t('f-com-total')}: ${total}`} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <Table size="small" sx={V4_TABLE} aria-label="health logs table">
              <TableHead sx={{ background: tokens.sidebar }}>
                <TableRow>
                  <TableCell>{t('f-animal-health-log-date')}</TableCell>
                  <TableCell>{t('f-animal-health-log-type')}</TableCell>
                  <TableCell>{t('f-animal-health-log-disease')}</TableCell>
                  <TableCell>{t('f-animal-health-log-medical-supply')}</TableCell>
                  <TableCell>{t('f-animal-health-log-location')}</TableCell>
                  <TableCell align="right">{t('f-animal-health-log-count')}</TableCell>
                  <TableCell align="center">{t('f-animal-health-log-treated')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length > 0 ? (
                  logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>{formatDateTime(log.dateTime)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          {renderTypeChip(log)}
                          {log.comment && (
                            <Tooltip arrow title={log.comment}>
                              <Icon fontSize="small" color="disabled">
                                comment
                              </Icon>
                            </Tooltip>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{renderDisease(log)}</TableCell>
                      <TableCell>{log.medicalSupply || '-'}</TableCell>
                      <TableCell>{log.location || '-'}</TableCell>
                      <TableCell align="right">{log.count ?? '-'}</TableCell>
                      <TableCell align="center">
                        {log.isTreated ? (
                          <Icon fontSize="small">check</Icon>
                        ) : (
                          <Icon fontSize="small">hourglass_empty</Icon>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {isBusy ? '…' : t('f-animal-health-log-empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AnimalViewHealthLogs;
